import { useState, useEffect } from "react";
import { getErrorMessage } from "@/lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { GraduationCap, Plus, Pencil, Trash2, ImageIcon } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface EducationItem {
  id: string;
  institution: string;
  degree: string;
  category: string;
  status: string | null;
  gpa: string | null;
  highlights: string[];
  logo_url: string | null;
  sort_order: number;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

const Education = () => {
  const { t } = useLanguage();
  const { isAdmin, user } = useAuth();
  const [educations, setEducations] = useState<EducationItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedEdu, setSelectedEdu] = useState<EducationItem | null>(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [institution, setInstitution] = useState("");
  const [degree, setDegree] = useState("");
  const [category, setCategory] = useState("formal");
  const [status, setStatus] = useState("");
  const [gpa, setGpa] = useState("");
  const [highlightsText, setHighlightsText] = useState("");
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState("0");

  const fetchEducations = async () => {
    const { data, error } = await supabase
      .from("education")
      .select("*")
      .order("sort_order", { ascending: true });

    if (!error && data) {
      setEducations(data as EducationItem[]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchEducations();
  }, []);

  const resetForm = () => {
    setInstitution("");
    setDegree("");
    setCategory("formal");
    setStatus("");
    setGpa("");
    setHighlightsText("");
    setLogoFile(null);
    setLogoPreview(null);
    setSortOrder("0");
    setSelectedEdu(null);
  };

  const openAddDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (edu: EducationItem) => {
    setSelectedEdu(edu);
    setInstitution(edu.institution);
    setDegree(edu.degree);
    setCategory(edu.category);
    setStatus(edu.status || "");
    setGpa(edu.gpa || "");
    setHighlightsText((edu.highlights || []).join("\n"));
    setLogoPreview(edu.logo_url);
    setLogoFile(null);
    setSortOrder(String(edu.sort_order));
    setDialogOpen(true);
  };

  const openDeleteDialog = (edu: EducationItem) => {
    setSelectedEdu(edu);
    setDeleteDialogOpen(true);
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setLogoPreview(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  const uploadLogo = async (file: File): Promise<string | null> => {
    const ext = file.name.split(".").pop();
    const fileName = `${crypto.randomUUID()}.${ext}`;
    const { error } = await supabase.storage
      .from("education-logos")
      .upload(fileName, file);

    if (error) {
      console.error("Upload error:", error);
      return null;
    }

    const { data } = supabase.storage
      .from("education-logos")
      .getPublicUrl(fileName);
    return data.publicUrl;
  };

  const deleteStorageFile = async (url: string) => {
    try {
      const parts = url.split("/education-logos/");
      if (parts[1]) {
        await supabase.storage.from("education-logos").remove([parts[1]]);
      }
    } catch (e) {
      console.error("Error deleting file:", e);
    }
  };

  const handleSave = async () => {
    if (!institution.trim() || !degree.trim()) {
      toast.error("Institusi dan gelar wajib diisi");
      return;
    }

    setSaving(true);
    try {
      let logoUrl = selectedEdu?.logo_url || null;

      if (logoFile) {
        if (selectedEdu?.logo_url) {
          await deleteStorageFile(selectedEdu.logo_url);
        }
        logoUrl = await uploadLogo(logoFile);
        if (!logoUrl) {
          toast.error("Gagal mengupload logo");
          setSaving(false);
          return;
        }
      }

      const highlights = highlightsText
        .split("\n")
        .map((h) => h.trim())
        .filter((h) => h.length > 0);

      const eduData = {
        institution: institution.trim(),
        degree: degree.trim(),
        category,
        status: status.trim() || null,
        gpa: gpa.trim() || null,
        highlights,
        logo_url: logoUrl,
        sort_order: parseInt(sortOrder) || 0,
      };

      if (selectedEdu) {
        const { error } = await supabase
          .from("education")
          .update(eduData)
          .eq("id", selectedEdu.id);
        if (error) throw error;
        toast.success("Pendidikan berhasil diperbarui");
      } else {
        const { error } = await supabase
          .from("education")
          .insert({ ...eduData, created_by: user?.id });
        if (error) throw error;
        toast.success("Pendidikan berhasil ditambahkan");
      }

      setDialogOpen(false);
      resetForm();
      fetchEducations();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedEdu) return;
    try {
      if (selectedEdu.logo_url) {
        await deleteStorageFile(selectedEdu.logo_url);
      }
      const { error } = await supabase
        .from("education")
        .delete()
        .eq("id", selectedEdu.id);
      if (error) throw error;
      toast.success("Pendidikan berhasil dihapus");
      setDeleteDialogOpen(false);
      setSelectedEdu(null);
      fetchEducations();
    } catch (error) {
      toast.error(getErrorMessage(error, "Gagal menghapus pendidikan"));
    }
  };

  const formalEducation = educations.filter((e) => e.category === "formal");
  const blockchainEducation = educations.filter((e) => e.category === "blockchain");

  const renderCard = (edu: EducationItem, index: number) => (
    <Card
      key={edu.id}
      className="shadow-soft hover:shadow-medium transition-all duration-300"
    >
      <CardHeader className="pb-3 sm:pb-4">
        <div className="flex items-start gap-3 sm:gap-4">
          <div className="flex-shrink-0">
            {edu.logo_url ? (
              <img
                src={edu.logo_url}
                alt={`${edu.institution} logo`}
                className="h-12 w-12 sm:h-14 sm:w-14 md:h-16 md:w-16 object-contain"
              />
            ) : (
              <div className="p-2 sm:p-3 rounded-lg bg-primary/10">
                <GraduationCap className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div>
                <CardTitle className="text-base sm:text-lg md:text-xl mb-1 sm:mb-2">
                  {edu.institution}
                </CardTitle>
                <p className="text-sm sm:text-base text-foreground font-medium">
                  {edu.degree}
                </p>
                <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                  {edu.status}
                </p>
              </div>
              {isAdmin && (
                <div className="flex gap-1 shrink-0">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => openEditDialog(edu)}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-destructive hover:text-destructive"
                    onClick={() => openDeleteDialog(edu)}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </CardHeader>
      {edu.highlights && edu.highlights.length > 0 && (
        <CardContent>
          <ul className="space-y-2">
            {edu.highlights.map((highlight, i) => (
              <li
                key={i}
                className="text-sm text-muted-foreground flex items-start gap-2"
              >
                <span className="text-primary mt-1">•</span>
                <span>{highlight}</span>
              </li>
            ))}
          </ul>
        </CardContent>
      )}
    </Card>
  );

  return (
    <section id="education" className="py-12 sm:py-16 md:py-20 relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        <div className="text-center mb-8 sm:mb-10 md:mb-12 animate-fade-up">
          <h2 className="text-3xl sm:text-4xl font-bold mb-3 sm:mb-4">
            {t.education.title}
          </h2>
          <p className="text-sm sm:text-base text-muted-foreground max-w-2xl mx-auto">
            {t.education.subtitle}
          </p>
        </div>

        {isAdmin && (
          <div className="flex justify-center mb-6">
            <Button onClick={openAddDialog} className="gap-2">
              <Plus className="h-4 w-4" />
              Tambah Pendidikan
            </Button>
          </div>
        )}

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5 md:gap-6 max-w-6xl mx-auto">
            {[1, 2].map((i) => (
              <Card key={i} className="animate-pulse">
                <CardContent className="p-6 space-y-3">
                  <div className="h-16 w-16 bg-muted rounded" />
                  <div className="h-4 bg-muted rounded w-3/4" />
                  <div className="h-3 bg-muted rounded w-1/2" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : educations.length === 0 ? (
          <div className="max-w-4xl mx-auto">
            <Card className="shadow-soft animate-fade-up">
              <CardContent className="p-6 sm:p-8 md:p-12 text-center">
                <div className="max-w-md mx-auto">
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary/10 mb-4">
                    <GraduationCap className="w-7 h-7 text-primary" />
                  </div>
                  <h3 className="text-xl font-bold mb-2">Belum ada data pendidikan</h3>
                  <p className="text-sm text-muted-foreground">
                    Data pendidikan belum ditambahkan.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        ) : (
          <Tabs defaultValue="formal" className="max-w-6xl mx-auto">
            <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 mb-8">
              <TabsTrigger value="formal">Formal Education</TabsTrigger>
              <TabsTrigger value="blockchain">Blockchain/Trading</TabsTrigger>
            </TabsList>

            <TabsContent value="formal">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5 md:gap-6">
                {formalEducation.map((edu, index) => renderCard(edu, index))}
                {formalEducation.length === 0 && (
                  <p className="text-muted-foreground text-center col-span-2 py-8">
                    Belum ada data pendidikan formal.
                  </p>
                )}
              </div>
            </TabsContent>

            <TabsContent value="blockchain">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5 md:gap-6">
                {blockchainEducation.map((edu, index) => renderCard(edu, index))}
                {blockchainEducation.length === 0 && (
                  <p className="text-muted-foreground text-center col-span-2 py-8">
                    Belum ada data pendidikan blockchain/trading.
                  </p>
                )}
              </div>
            </TabsContent>
          </Tabs>
        )}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedEdu ? "Edit Pendidikan" : "Tambah Pendidikan Baru"}
            </DialogTitle>
            <DialogDescription>
              {selectedEdu
                ? "Perbarui informasi pendidikan"
                : "Isi detail pendidikan yang ingin ditambahkan"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label htmlFor="edu-institution">Institusi *</Label>
              <Input
                id="edu-institution"
                value={institution}
                onChange={(e) => setInstitution(e.target.value)}
                placeholder="Masukkan nama institusi"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edu-degree">Gelar / Program *</Label>
              <Input
                id="edu-degree"
                value={degree}
                onChange={(e) => setDegree(e.target.value)}
                placeholder="Masukkan gelar atau program studi"
              />
            </div>

            <div className="space-y-2">
              <Label>Kategori</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="formal">Formal Education</SelectItem>
                  <SelectItem value="blockchain">Blockchain/Trading</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edu-status">Status</Label>
                <Input
                  id="edu-status"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  placeholder="Masukkan status (Graduated / Completed)"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edu-gpa">GPA</Label>
                <Input
                  id="edu-gpa"
                  value={gpa}
                  onChange={(e) => setGpa(e.target.value)}
                  placeholder="Masukkan GPA"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edu-sort">Urutan</Label>
              <Input
                id="edu-sort"
                type="number"
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                placeholder="Masukkan urutan"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edu-logo">Logo Institusi</Label>
              <Input
                id="edu-logo"
                type="file"
                accept="image/*"
                onChange={handleLogoChange}
              />
              {logoPreview && (
                <div className="mt-2">
                  <img
                    src={logoPreview}
                    alt="Preview"
                    className="h-16 w-16 object-contain border rounded-lg p-1"
                  />
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="edu-highlights">Highlights (satu per baris)</Label>
              <Textarea
                id="edu-highlights"
                value={highlightsText}
                onChange={(e) => setHighlightsText(e.target.value)}
                placeholder="Masukkan highlights"
                rows={4}
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => setDialogOpen(false)}
                disabled={saving}
              >
                Batal
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? "Menyimpan..." : "Simpan"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus Pendidikan</AlertDialogTitle>
            <AlertDialogDescription>
              Apakah Anda yakin ingin menghapus "{selectedEdu?.institution}"?
              Tindakan ini tidak dapat dibatalkan.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Hapus
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
};

export default Education;
